import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { IconButton, Tooltip } from "@mui/material";
import { Form } from "formik";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { shade } from "@skilitics/designer-theme";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { LinkIconButton } from "../../buttons/LinkIconButton";
import { Loading, LoadingContainer } from "../../common";
import { Icon } from "../../icons";
import { transientOptions } from "../../utilities";
import type { FileDropzoneProps, MediaAssetValue } from "../controls";
import { FormikForm } from "../FormikForm";
import { FormRowField } from "../FormRow";
import {
  componentTypeByMediaAssetType,
  maximumFilesizeByMediaAssetType,
  uploadOptionsByMediaAssetType,
} from "./constants";
import type { MediaButtonsProps } from "./types";

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  width: 100%;

  color: ${({ theme }) => theme.alwaysWhite};

  padding: 8px;
`;

const UploadContainer = styled("span", transientOptions)<{ $busy: boolean }>`
  position: relative;
  height: 100%;
  overflow: hidden;

  & ${LoadingContainer} {
    color: ${({ theme }) => theme.alwaysWhite};
    overflow: visible;
  }

  ${({ $busy }) =>
    $busy &&
    css`
      cursor: default;
      pointer-events: none;
    `}
`;

const UploadButton = styled(IconButton)`
  height: 28px;

  color: ${({ theme }) => theme.alwaysWhite};
  background-color: ${({ theme }) => theme.neutralContainer};

  border-radius: ${({ theme }) => theme.borderRadius}px;

  transition: background-color 300ms ease-in-out;

  &:has(+ div:hover) {
    background-color: ${({ theme }) => shade(theme.neutralContainer)};
  }
`;

const UploadField = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  overflow: hidden;
`;

const DownloadContainer = styled("span", transientOptions)<{ $available: boolean }>`
  display: flex;
  align-items: center;
  height: 28px;

  color: ${({ theme }) => theme.alwaysWhite};
  background-color: ${({ theme }) => theme.neutralContainer};

  border-radius: ${({ theme }) => theme.borderRadius}px;

  opacity: ${({ $available }) => ($available ? 1 : 0.5)};
  pointer-events: ${({ $available }) => ($available ? "auto" : "none")};

  transition:
    opacity 300ms ease-in-out,
    background-color 300ms ease-in-out;

  &:hover {
    background-color: ${({ theme }) => shade(theme.neutralContainer)};
  }
`;

type Values = {
  media?: MediaAssetValue;
};

export const MediaButtons = ({ url = "", type = "image", onUpload }: MediaButtonsProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const [busy, setBusy] = useState(false);

  const ComponentType = componentTypeByMediaAssetType[type];

  const maximumFileSize = maximumFilesizeByMediaAssetType[type];

  const handleSubmit = useCallback(
    (values: Values) => {
      onUpload?.(values.media);
      setBusy(false);
    },
    [onUpload],
  );

  const onFileSelected = useCallback(
    async (file: File) => {
      setBusy(true);
      return uploadOptionsByMediaAssetType[type].onFileSelected?.(file) ?? Promise.resolve({});
    },
    [type],
  );

  const handleFileRejected: NonNullable<FileDropzoneProps["onFileRejected"]> = useCallback(({ reason, onHandled }) => {
    toast.error(reason);
    onHandled();
  }, []);

  return (
    <ButtonsContainer>
      {onUpload && (
        <Tooltip title={formatMessage("upload")} placement="top">
          <UploadContainer $busy={busy}>
            <UploadButton>
              {busy && <Loading size={16} />}
              {!busy && <Icon icon="arrow-right-arrow-left" size={14} />}
            </UploadButton>

            <UploadField>
              <FormikForm<Values> initialValues={{}} onSubmit={handleSubmit} submitOnBlur dataTest="upload-form">
                <Form>
                  <FormRowField name="media">
                    <ComponentType
                      type={type}
                      uploadOptions={{ ...uploadOptionsByMediaAssetType[type], onFileSelected }}
                      maximumFileSize={maximumFileSize}
                      onFileRejected={handleFileRejected}
                      empty
                    />
                  </FormRowField>
                </Form>
              </FormikForm>
            </UploadField>
          </UploadContainer>
        </Tooltip>
      )}

      <Tooltip title={formatMessage("download")} placement="top">
        <DownloadContainer $available={!!url}>
          <LinkIconButton
            disabled={busy}
            to={url}
            target="_blank"
            rel="noopener noreferrer"
            data-test="download-button"
          >
            <Icon icon="download" size={14} />
          </LinkIconButton>
        </DownloadContainer>
      </Tooltip>
    </ButtonsContainer>
  );
};

const formatStrings = {
  upload: {
    en: "Replace",
  },
  download: {
    en: "Download",
  },
} satisfies FormatMessages;
